import Post from 'app/utils/Post';
import type {ParamsInterface} from 'app/utils/Get';

export type LobbyName = 'cup' | 'poker';
export type LobbyEvent = 'unavailable' | 'recovered' | 'retry';
type WatcherStatus = 'idle' | 'waiting' | 'alive' | 'unavailable';

export interface LobbyAvailabilityOptions {
    embedded: boolean
    getLobbyOrigin: () => string
    lobby: LobbyName
    onRecovered: () => void
    onUnavailable: () => void
    timeoutMs?: number
}

interface NavigatorWithConnection extends Navigator {
    connection?: {effectiveType?: string}
}

export const LOBBY_UNAVAILABLE_TIMEOUT_MS = 15000;
export const LOBBY_EVENTS_URL = '/lobby_events';
const EMBEDDED_REPORT_KEY_PREFIX = 'lobby-events:embedded:';

export class LobbyAvailabilityWatcher {
    private status: WatcherStatus = 'idle';

    private attempt = 0;

    private startedAt = 0;

    private iframeLoadedAt: number | null = null;

    private timeoutId: number | null = null;

    private readonly options: LobbyAvailabilityOptions;

    constructor(options: LobbyAvailabilityOptions) {
        this.options = options;
    }

    start(): void {
        if (this.status === 'idle') {
            this.arm();
        }
    }

    markIframeLoaded(): void {
        if (this.iframeLoadedAt === null) {
            this.iframeLoadedAt = Date.now();
        }
    }

    markAlive(): void {
        if (this.status === 'idle' || this.status === 'alive') {
            return;
        }

        const wasUnavailable = this.status === 'unavailable';

        this.clearTimer();
        this.status = 'alive';

        if (wasUnavailable) {
            this.report('recovered');
            this.options.onRecovered();
        }
    }

    retry(): number {
        this.attempt += 1;
        this.clearTimer();
        this.arm();
        this.report('retry');

        return this.attempt;
    }

    stop(): void {
        this.clearTimer();
        this.status = 'idle';
    }

    private arm(): void {
        this.status = 'waiting';
        this.startedAt = Date.now();
        this.iframeLoadedAt = null;
        this.timeoutId = window.setTimeout(this.handleTimeout, this.options.timeoutMs ?? LOBBY_UNAVAILABLE_TIMEOUT_MS);
    }

    private clearTimer(): void {
        if (this.timeoutId !== null) {
            window.clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    private handleTimeout = (): void => {
        this.timeoutId = null;
        this.status = 'unavailable';
        this.report('unavailable');
        this.options.onUnavailable();
    };

    private buildPayload(event: LobbyEvent): ParamsInterface {
        const {connection} = navigator as NavigatorWithConnection;

        return {
            attempt: this.attempt,
            connection: connection?.effectiveType ?? null,
            elapsed_ms: Date.now() - this.startedAt,
            embedded: this.options.embedded,
            event,
            iframe_load_ms: this.iframeLoadedAt === null ? null : this.iframeLoadedAt - this.startedAt,
            iframe_loaded: this.iframeLoadedAt !== null,
            lobby: this.options.lobby,
            lobby_origin: this.options.getLobbyOrigin(),
            online: navigator.onLine
        };
    }

    private alreadyReportedEmbedded(): boolean {
        const key = `${EMBEDDED_REPORT_KEY_PREFIX}${this.options.lobby}`;

        try {
            if (window.sessionStorage.getItem(key) !== null) {
                return true;
            }

            window.sessionStorage.setItem(key, '1');
        } catch {
            return false;
        }

        return false;
    }

    private report(event: LobbyEvent): void {
        if (this.options.embedded && event === 'unavailable' && this.attempt === 0 && this.alreadyReportedEmbedded()) {
            return;
        }

        const params = this.buildPayload(event);

        Promise.resolve()
            .then(() => new Post({params, url: LOBBY_EVENTS_URL}).execute())
            .catch(() => null);
    }
}
